import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFleetAuth } from "../../auth/fleetAuth";
import type { SignInOptions, SignInPath } from "../../auth/fleetAuth";
import CircularProgress from "@mui/material/CircularProgress";
import styled from "styled-components";
import { usePermissions } from "../../contexts/PermissionsContext";
import SignIn, { SHOWS_BOTH_DOORS } from "../../pages/SignIn/SignIn";
import { describeSignInError } from "../../pages/SignIn/signInError";

interface RequireAuthProps {
  children: React.ReactNode;
}

/**
 * The app-wide session gate: no session, no page.
 *
 * With no session it renders the SignIn page and waits. It used to redirect
 * to the identity provider by itself the moment the provider settled, and
 * that automatic redirect was the root of two failures (SW-6866):
 *
 * - A failed callback -- an invalid or stale `state`, an expired code,
 *   `access_denied` -- settles as {isLoading: false, isAuthenticated: false,
 *   error: set}. Treated like any other logged-out state, it launched another
 *   redirect at once: a transient failure looked like a mysterious bounce
 *   through the identity provider, a persistent one was an infinite loop.
 * - Nothing stopped a second redirect while the first was still navigating,
 *   and Auth0 keeps one login transaction per client, so the second overwrote
 *   the first's state / code_verifier and the completing redirect came back as
 *   "Invalid state".
 *
 * A page that starts a sign-in only on a click has neither problem, and it
 * lets the console ask the one question the identity provider's own page
 * cannot: are you an AeroVect employee (Google Workspace) or a client
 * (email + password)? The answer picks the right screen; see SignInPath.
 *
 * A callback error is shown on that page in plain words (signInError.ts) and
 * the next attempt is the person's decision.
 *
 * WHEN THERE IS ONLY ONE WAY IN, THE PAGE IS SKIPPED (SW-7116). A build that
 * shows one door has nothing to ask, so it starts that sign-in itself and shows
 * the spinner while the browser leaves. A Delta operator on the Auth0 console
 * therefore lands on the provider's own page, as they did before the two-door
 * screen shipped.
 *
 * That is an automatic redirect, so it carries both SW-6866 guards explicitly,
 * and they are the whole reason this is safe:
 *
 * - It never starts while `error` is set. A failed callback is exactly the
 *   state that used to relaunch a login and loop; that case still renders the
 *   page, in plain words, and the retry is the person's click.
 * - It starts AT MOST ONCE per mount, held by a ref rather than by state, so a
 *   re-render during the navigation cannot begin a second transaction and
 *   overwrite the first one's state and code_verifier.
 *
 * Both doors, and therefore the question, come back with the provider flip.
 */
const RequireAuth = ({ children }: RequireAuthProps) => {
  const {
    isLoading,
    isAuthenticated,
    error: authError,
    login,
  } = useFleetAuth();
  const { isLoading: permissionsLoading } = usePermissions();
  const [redirectError, setRedirectError] = useState<string | null>(null);

  // One destination means no question worth asking: start it and show the
  // spinner. See the note above for the two guards this depends on.
  const autoStarted = useRef(false);

  const choose = useCallback(
    async (path: SignInPath, options?: SignInOptions) => {
      setRedirectError(null);
      try {
        // Exactly the arguments given: a provider's login() is mocked in tests
        // and asserted on, and a trailing undefined is a different call.
        await (options ? login(path, options) : login(path));
      } catch (err) {
        console.error("Failed to redirect to login:", err);
        setRedirectError(
          "Unable to open the sign-in page. Check your connection and try again.",
        );
        // Re-throw so the SignIn page releases its "opening…" state.
        throw err;
      }
    },
    [login],
  );

  const settledSignedOut = !isLoading && !isAuthenticated;
  const failed = Boolean(authError) || Boolean(redirectError);
  const startsItself = settledSignedOut && !failed && !SHOWS_BOTH_DOORS;

  useEffect(() => {
    if (!startsItself || autoStarted.current) return;
    // Latched before the call, never cleared: a rejected redirect sets
    // redirectError, which renders the page and hands the retry back to the
    // person. Nothing here may start a second transaction.
    autoStarted.current = true;
    void choose("employee").catch(() => undefined);
  }, [startsItself, choose]);

  // Hold the shell until permissions are known too, not just the session.
  // Every page renders the Navbar, whose membership is permission-derived, so
  // releasing children the moment the provider settles let the nav mount with
  // an unresolved (empty) permission set and then pop the permissioned links in
  // a few hundred ms later. One gate, one render, final state.
  if (isAuthenticated && !permissionsLoading) {
    return <>{children}</>;
  }

  // Two holds, one render: a live session whose permissions are still
  // resolving, and a provider still restoring a session from storage or
  // finishing a callback -- showing the sign-in page during the latter would
  // flash it at people who are already signed in.
  if (
    isAuthenticated ||
    isLoading ||
    startsItself ||
    (autoStarted.current && !failed)
  ) {
    return (
      <CenteredContainer>
        <CircularProgress size={40} />
        <LoadingText>Loading…</LoadingText>
      </CenteredContainer>
    );
  }

  const message = authError ? describeSignInError(authError) : redirectError;
  return <SignIn onChoose={choose} error={message} />;
};

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 16px;
`;

const LoadingText = styled.p`
  color: ${(props) => props.theme.colors.grey};
  font-size: 14px;
`;

export default RequireAuth;
